import { TableRow, TableCell, Switch } from '@heroui/react';
import React from 'react';
import { FiMessageCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  LoadingTable,
  LoadingTableColumn,
  LinkCell,
} from '../../../../../components/Table';
import { ChatListItem } from '../../../types/chatList';

interface ChatsTableProps {
  chats: readonly ChatListItem[];
  onChatToggle: (data: { id: string; enabled: boolean }) => void;
  isLoading?: boolean;
}

export const ChatsTable: React.FC<ChatsTableProps> = ({
  chats,
  onChatToggle,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const loadingTableColumns: LoadingTableColumn[] = [
    { label: 'Buyer', className: 'pl-6' },
    { label: 'Buyer users', className: 'hidden md:table-cell' },
    { label: 'Supplier', className: 'hidden lg:table-cell' },
    { label: 'Supplier users', className: 'hidden lg:table-cell' },
    { label: 'Chat name' },
    { label: 'Restaurant name', className: 'hidden md:table-cell' },
    { label: 'Customer number', className: 'hidden lg:table-cell' },
    { label: 'Delivery address', className: 'hidden xl:table-cell' },
    { label: 'Enabled?', className: 'pr-6' },
  ];

  const handleRowClick = (chatId: string, event: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = event.target as HTMLElement;
    if (target.closest('button, [role="switch"], input')) {
      return;
    }
    navigate(`/chats/${chatId}`);
  };

  const renderRow = (chat: ChatListItem) => (
    <TableRow
      key={chat.id}
      className="hover:bg-default-50 dark:hover:bg-default-100/50 transition-colors duration-200 cursor-pointer"
      onClick={(event) => handleRowClick(chat.id, event)}
    >
      <TableCell className="pl-6">
        {chat.buyerEntity ? (
          <LinkCell to={`/buyers/${chat.buyerEntity.id}`}>
            {chat.buyerEntity.name}
          </LinkCell>
        ) : (
          <span className="text-default-500">-</span>
        )}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div className="space-y-1">
          {chat.buyerUsers.length > 0 ? (
            chat.buyerUsers.map((user) => (
              <div key={user.id} className="text-sm">
                <LinkCell to={`/users/${user.id}`} className="text-sm">
                  {user.name}
                </LinkCell>
              </div>
            ))
          ) : (
            <span className="text-default-500">-</span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {chat.supplierEntity ? (
          <LinkCell to={`/suppliers/${chat.supplierEntity.id}`}>
            {chat.supplierEntity.name}
          </LinkCell>
        ) : (
          <span className="text-default-500">-</span>
        )}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <div className="space-y-1">
          {chat.supplierUsers.length > 0 ? (
            chat.supplierUsers.map((user) => (
              <div key={user.id} className="text-sm">
                <LinkCell to={`/users/${user.id}`} className="text-sm">
                  {user.name}
                </LinkCell>
              </div>
            ))
          ) : (
            <span className="text-default-500">-</span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <span className="font-medium">{chat.chatName}</span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {chat.restaurantName}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {chat.customerNumber || '-'}
      </TableCell>
      <TableCell className="hidden xl:table-cell">
        <div className="max-w-xs truncate" title={chat.address}>
          {chat.address || '-'}
        </div>
      </TableCell>
      <TableCell className="pr-6">
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            key={`${chat.id}-${chat.isEnabled}`}
            isSelected={chat.isEnabled}
            onValueChange={(checked) => {
              onChatToggle({ id: chat.id, enabled: checked });
            }}
            aria-label={chat.isEnabled ? 'Disable chat' : 'Enable chat'}
            size="sm"
            color="success"
            data-selected={chat.isEnabled}
            classNames={{
              base: 'opacity-100',
              wrapper:
                'group-data-[selected=true]:!bg-success-500 bg-default-300',
              thumb:
                'group-data-[selected=true]:!translate-x-full bg-white shadow-md',
            }}
          />
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={loadingTableColumns}
      ariaLabel="Chats table"
      className="mb-6"
      isLoading={isLoading}
      isEmpty={chats.length === 0}
      data={[...chats]}
      renderRow={(chat) => renderRow(chat as ChatListItem)}
      emptyState={{
        icon: FiMessageCircle,
        iconBgClass: 'bg-primary-50',
        iconColorClass: 'text-primary-500',
        title: 'No chats found',
        description: 'Try adjusting your search or filters.',
      }}
    />
  );
};
